import React, { type ReactNode, type ComponentType } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SyncIcon from "@material-ui/icons/Sync";

import { userPlanSelector } from "src/store/system/selector";
import { USER_PLAN as UserPlan } from "src/enums/common.enum";
import { StatefulLoadableIconButton } from "src/components/LoadableIconButton";
import { userRoleSelector } from "src/store/system/selector";

const useStyles = makeStyles((theme) => ({
  iconButton: {
    "&:hover, &.MuiIconButton-root:hover": {
      backgroundColor: "transparent",
    },
    "& svg": {
      transition: "transform 0.2s ease",
    },
    "&:hover svg": {
      transform: "scale(1.25)",
    },
  },
  errorIconButton: {
    color: theme.palette.error.main,
  },
}));

function PlatformIntegrationsTableRow({
  name,
  platform,
  isRunning,
  sync,
  inventory,
  onImport,
  onSyncChanged,
  onPullDataNow,
  hideInventory,
  showInventoryCheckbox = true,
  showReconnectAction = false,
  onOpenNotifications,
  onAdd,
  onDelete,
  AddComponent,
}: {
  name: string;
  platform: ReactNode;
  isRunning: boolean;
  sync: boolean;
  inventory: boolean;
  hideInventory: boolean;
  showInventoryCheckbox?: boolean;
  showReconnectAction?: boolean;
  onOpenNotifications?: () => void;
  onImport: () => void;
  onSyncChanged: (sync: boolean, inventory?: boolean) => void;
  onPullDataNow: () => Promise<void>;
  onAdd?: () => void;
  onDelete?: () => void;
  AddComponent?: ComponentType;
}) {
  const classes = useStyles();
  const plan = useSelector(userPlanSelector) || UserPlan.BASIC;
  const planUltimate = plan === UserPlan.ULTIMATE;
  const userRole = useSelector(userRoleSelector);
  const reconnectTooltipText =
    'Click the "Reconnect Required" button to sign back into this account.';
  const importButton = (
    <Button
      color="primary"
      onClick={() => onImport()}
      disabled={isRunning || showReconnectAction}
    >
      Import
    </Button>
  );
  const syncButton = (
    <StatefulLoadableIconButton
      onClick={async () => {
        if (!planUltimate) {
          toast.error(
            "Only ultimate users are allowed to turn on Daily Integration. Please upgrade to unlock this feature"
          );
          return;
        }
        await onPullDataNow();
      }}
      color="primary"
      className={classes.iconButton}
      disabled={!sync || isRunning}
    >
      <SyncIcon />
    </StatefulLoadableIconButton>
  );
  const handleSyncChanged = (s: boolean, i?: boolean) => {
    if (!planUltimate) {
      toast.error(
        "Only ultimate users are allowed to turn on Daily Integration. Please upgrade to unlock this feature"
      );
      return;
    }

    onSyncChanged(s, i);
  };

  return (
    <TableRow>
      <TableCell>{name}</TableCell>
      <TableCell>{platform}</TableCell>
      <TableCell align="center">
        {showReconnectAction ? (
          <Tooltip title={reconnectTooltipText}>
            <span>{importButton}</span>
          </Tooltip>
        ) : isRunning ? (
          <Tooltip title="Your import is currently processing.">
            <div>{importButton}</div>
          </Tooltip>
        ) : (
          importButton
        )}
      </TableCell>
      <TableCell align="center">
        {showReconnectAction ? (
          <Tooltip title={reconnectTooltipText}>
            <span>
              <Checkbox
                checked={false}
                disabled={true}
                onChange={(ev) => handleSyncChanged(ev.target.checked)}
              />
            </span>
          </Tooltip>
        ) : (
          <Checkbox
            checked={planUltimate && (sync || false)}
            disabled={userRole === "viewer"}
            onChange={(ev) => handleSyncChanged(ev.target.checked)}
          />
        )}
      </TableCell>
      {hideInventory ? null : (
        <TableCell align="center">
          {showInventoryCheckbox && (
            <Checkbox
              disabled={!sync || userRole === "viewer" || showReconnectAction}
              checked={planUltimate && sync && inventory}
              onChange={(ev) =>
                handleSyncChanged(sync || false, ev.target.checked)
              }
            />
          )}
        </TableCell>
      )}
      <TableCell align="center">
        {showReconnectAction ? (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (onOpenNotifications) onOpenNotifications();
            }}
          >
            Reconnect Required
          </Button>
        ) : (
          <>
            {isRunning ? (
              <Tooltip title="Your import is currently processing.">
                <div>{syncButton}</div>
              </Tooltip>
            ) : (
              syncButton
            )}
          </>
        )}
      </TableCell>
      <TableCell align="center">
        {AddComponent ? (
          <AddComponent />
        ) : onAdd ? (
          <IconButton
            id={"add-integration-" + platform}
            color="primary"
            className={classes.iconButton}
            onClick={() => onAdd()}
            disabled={userRole === "viewer"}
          >
            <AddIcon />
          </IconButton>
        ) : null}
      </TableCell>
      <TableCell align="center">
        {onDelete ? (
          <IconButton
            className={`${classes.iconButton} ${classes.errorIconButton}`}
            onClick={() => onDelete()}
            disabled={userRole === "viewer"}
          >
            <DeleteIcon />
          </IconButton>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

export default PlatformIntegrationsTableRow;
